import { useMemo, useState, type ReactNode } from 'react';
import {
  FEATURE_GROUP_LABELS,
  SCHEDULE_SCOPE_LABELS,
  GLOBAL_PRIVILEGES,
  GLOBAL_PRIVILEGE_LABELS,
  READ_ONLY_GROUPS,
  type CreateInviteInput,
  type FeatureGroup,
  type GlobalPrivilege,
  type House,
  type Invite,
  type Permissions,
  type Room,
  type ScheduleScope,
} from '@guest/shared';
import { Icon } from '../lib/icons.js';
import { Panel } from '../components/ui.js';

function PillCheck({
  on,
  onToggle,
  children,
}: {
  on: boolean;
  onToggle: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="pill-check" data-on={on}>
      <input type="checkbox" checked={on} onChange={onToggle} />
      <span className="tick">
        <Icon name="check" size={9} strokeWidth={3} />
      </span>
      {children}
    </label>
  );
}

/** Datetime-local wants a local wall-clock string, not an ISO instant. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value: string): string {
  return new Date(value).toISOString();
}

function defaultWindow(): { startsAt: string; endsAt: string } {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 3);
  return { startsAt: start.toISOString(), endsAt: end.toISOString() };
}

export interface EditorValue extends CreateInviteInput {}

export function InviteEditor({
  house,
  initial,
  onSubmit,
  onCancel,
  busy,
}: {
  house: House;
  initial?: Invite;
  onSubmit: (value: EditorValue) => void;
  onCancel: () => void;
  busy: boolean;
}): ReactNode {
  const win = useMemo(defaultWindow, []);
  const [guestName, setGuestName] = useState(initial?.guestName ?? '');
  const [guestRoomId, setGuestRoomId] = useState<string | null>(initial?.guestRoomId ?? null);
  const [startsAt, setStartsAt] = useState(initial?.startsAt ?? win.startsAt);
  const [endsAt, setEndsAt] = useState(initial?.endsAt ?? win.endsAt);
  const [note, setNote] = useState(initial?.note ?? '');
  const [scheduleScope, setScheduleScope] = useState<ScheduleScope>(initial?.scheduleScope ?? 'none');
  const [includesMaster, setIncludesMaster] = useState(initial?.scheduleIncludesMaster ?? false);
  const [permissions, setPermissions] = useState<Permissions>(
    initial?.permissions ?? { globals: ['alarm_view', 'alarm_arm', 'wifi'], rooms: [] },
  );

  const grantedGroups = (roomId: string): FeatureGroup[] =>
    permissions.rooms.find((r) => r.roomId === roomId)?.groups ?? [];

  const setGroups = (roomId: string, groups: FeatureGroup[]): void => {
    setPermissions((prev) => {
      const rooms = prev.rooms.filter((r) => r.roomId !== roomId);
      if (groups.length > 0) rooms.push({ roomId, groups });
      return { ...prev, rooms };
    });
  };

  const toggleGroup = (roomId: string, group: FeatureGroup): void => {
    const current = grantedGroups(roomId);
    setGroups(
      roomId,
      current.includes(group) ? current.filter((g) => g !== group) : [...current, group],
    );
  };

  const roomGroupsOf = (room: Room): FeatureGroup[] => [...new Set(room.controls.map((c) => c.group))];

  const toggleWholeRoom = (room: Room): void => {
    const all = roomGroupsOf(room);
    setGroups(room.id, grantedGroups(room.id).length === all.length ? [] : all);
  };

  const toggleGlobal = (g: GlobalPrivilege): void => {
    setPermissions((prev) => ({
      ...prev,
      globals: prev.globals.includes(g) ? prev.globals.filter((x) => x !== g) : [...prev.globals, g],
    }));
  };

  /** One-tap starting points; most stays are one of these two shapes. */
  const applyPreset = (preset: 'guest' | 'sitter'): void => {
    const guestRoom = house.rooms.find((r) => r.id === guestRoomId);
    if (preset === 'guest') {
      const shared = house.rooms.filter((r) => r.kind === 'shared');
      setPermissions({
        globals: ['alarm_view', 'alarm_disarm', 'alarm_arm', 'front_door_unlock', 'wifi'],
        rooms: [
          ...(guestRoom ? [{ roomId: guestRoom.id, groups: roomGroupsOf(guestRoom) }] : []),
          ...shared.map((r) => ({ roomId: r.id, groups: roomGroupsOf(r) })),
        ],
      });
    } else {
      setPermissions({
        globals: [...GLOBAL_PRIVILEGES],
        rooms: house.rooms.map((r) => ({ roomId: r.id, groups: roomGroupsOf(r) })),
      });
    }
  };

  const valid = guestName.trim().length > 0 && Date.parse(endsAt) > Date.parse(startsAt);

  return (
    <div className="stack" style={{ gap: 'var(--space-3)' }}>
      <Panel title="Who and when" icon="clock" className="stack">
        <div className="stack" style={{ gap: 'var(--space-3)' }}>
          <div>
            <label className="field-label" htmlFor="guestName">
              Guest name
            </label>
            <input
              id="guestName"
              className="field"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Alex Whitfield"
            />
          </div>

          <div className="two-col">
            <div>
              <label className="field-label" htmlFor="startsAt">
                Access from
              </label>
              <input
                id="startsAt"
                type="datetime-local"
                className="field"
                value={toLocalInput(startsAt)}
                onChange={(e) => setStartsAt(fromLocalInput(e.target.value))}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="endsAt">
                Access until
              </label>
              <input
                id="endsAt"
                type="datetime-local"
                className="field"
                value={toLocalInput(endsAt)}
                onChange={(e) => setEndsAt(fromLocalInput(e.target.value))}
              />
            </div>
          </div>

          <div>
            <label className="field-label" htmlFor="guestRoom">
              Staying in
            </label>
            <select
              id="guestRoom"
              className="field"
              value={guestRoomId ?? ''}
              onChange={(e) => setGuestRoomId(e.target.value || null)}
            >
              <option value="">Not staying over</option>
              {house.rooms
                .filter((r) => r.kind === 'guest')
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="field-label" htmlFor="note">
              Note to self
            </label>
            <input
              id="note"
              className="field"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Looking after the cat"
            />
          </div>
        </div>
      </Panel>

      <Panel title="Presets" icon="sparkle" className="stack">
        <div className="chips">
          <button type="button" className="chip" onClick={() => applyPreset('guest')}>
            <Icon name="bed" size={15} />
            Overnight guest
          </button>
          <button type="button" className="chip" onClick={() => applyPreset('sitter')}>
            <Icon name="key" size={15} />
            House sitter — everything
          </button>
          <button
            type="button"
            className="chip"
            onClick={() => setPermissions({ globals: [], rooms: [] })}
          >
            <Icon name="close" size={15} />
            Clear all
          </button>
        </div>
      </Panel>

      <Panel title="House-wide" icon="shield" className="stack">
        <div className="grant-groups">
          {GLOBAL_PRIVILEGES.map((g) => (
            <PillCheck key={g} on={permissions.globals.includes(g)} onToggle={() => toggleGlobal(g)}>
              {GLOBAL_PRIVILEGE_LABELS[g]}
            </PillCheck>
          ))}
        </div>
      </Panel>

      <Panel title="Lighting timings" icon="clock" className="stack">
        <p className="editor-hint">
          Whether this guest can set when the lights brighten in the morning and soften in the
          evening. “Their room only” needs the room they are staying in to be set above.
        </p>
        <div className="grant-groups">
          {(['none', 'room', 'house'] as ScheduleScope[]).map((scope) => (
            <PillCheck
              key={scope}
              on={scheduleScope === scope}
              onToggle={() => setScheduleScope(scope)}
            >
              {SCHEDULE_SCOPE_LABELS[scope]}
            </PillCheck>
          ))}
        </div>
        {scheduleScope === 'room' && guestRoomId === null && (
          <p className="editor-hint" style={{ color: 'hsl(var(--caution))', margin: '8px 0 0' }}>
            No room selected, so this will have no effect.
          </p>
        )}
        {scheduleScope === 'house' && (
          <div style={{ marginTop: 10 }}>
            <PillCheck on={includesMaster} onToggle={() => setIncludesMaster((v) => !v)}>
              Also control the master suite
            </PillCheck>
            <p className="editor-hint" style={{ margin: '8px 0 0' }}>
              Off by default: a house sitter sets the times everywhere except your own bedroom.
            </p>
          </div>
        )}
      </Panel>

      <Panel title="Rooms" icon="rooms" className="stack">
        {house.rooms.map((room) => {
          const groups = roomGroupsOf(room);
          const granted = grantedGroups(room.id);
          return (
            <div className="grant-room" key={room.id}>
              <div className="grant-head">
                <span className="ctrl-icon" data-on={granted.length > 0}>
                  <Icon name={room.icon} size={17} />
                </span>
                <span className="nm">{room.name}</span>
                <button type="button" className="btn btn-ghost" onClick={() => toggleWholeRoom(room)}>
                  {granted.length === groups.length ? 'None' : 'All'}
                </button>
              </div>
              <div className="grant-groups">
                {groups.map((g) => (
                  <PillCheck
                    key={g}
                    on={granted.includes(g)}
                    onToggle={() => toggleGroup(room.id, g)}
                  >
                    {FEATURE_GROUP_LABELS[g]}
                    {READ_ONLY_GROUPS.includes(g) && (
                      <span style={{ opacity: 0.6, fontSize: 11 }}>· view</span>
                    )}
                  </PillCheck>
                ))}
              </div>
            </div>
          );
        })}
      </Panel>

      <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-primary"
          disabled={!valid || busy}
          onClick={() =>
            onSubmit({
              guestName: guestName.trim(),
              guestRoomId,
              startsAt,
              endsAt,
              permissions,
              scheduleScope,
              scheduleIncludesMaster: includesMaster,
              note,
            })
          }
        >
          {busy ? 'Saving…' : initial ? 'Save changes' : 'Create invite'}
        </button>
      </div>
    </div>
  );
}
